import * as React from 'react';
import { ConfigContext } from 'modules/context';
import { ITranslationProps, translate } from 'components/translate-hoc';

import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';
import {
    INITIAL_SUCCESS_ANALYTICS_DATA,
    ON_SUBMIT_ANOTHER_CODE_ANALYTICS_DATA
} from '../../../redemption/redemption-success/redemption-success.constants';
import { getTrackingFlowSubType } from 'utils/analytics';
import { IRedemptionSuccessProps } from './redemption-success.types';
import { TEXT_KEYS } from '../../../redemption/redemption.constants';
import ResubmitCodeButton from '../../../resubmit-code-button';
import { IEntitlement } from '@sitegen/common/types/entitlements';

export class RedemptionSuccess extends React.Component<
IRedemptionSuccessProps & ITranslationProps
> {
    public componentDidMount() {
        const { config: { widgetName } } = this.context;
        return AnalyticsService.sendFlowEvent({
            ...INITIAL_SUCCESS_ANALYTICS_DATA,
            flowSubType: getTrackingFlowSubType(widgetName),
            platformSelection: this.props.platform
        });
    }

    public render(): JSX.Element {
        const { dangerouslyTranslateWithHTML: translateKey, benefits } = this.props;
        const { getModeClass, config: { widgetName } } = this.context;

        return (
            <div className={getModeClass('sitegen-redeem-success')}>
                <div className="sitegen-redeem-success__title">
                    {translateKey(TEXT_KEYS.UNLOCK_YOUR_CONTENT)}
                </div>
                { benefits ? this.renderEntitlements() : this.renderRewardPlaceholder() }
                <div className="sitegen-redeem-success__button-container">
                    <ResubmitCodeButton
                        additionalAnalyticsInfo={{
                            ...ON_SUBMIT_ANOTHER_CODE_ANALYTICS_DATA,
                            flowSubType: getTrackingFlowSubType(widgetName)
                        }}
                    />
                </div>
            </div>
        );
    }

    public renderEntitlements(): JSX.Element | null {
        const { config: { entitlements = [] } } = this.context;
        const {
            originalTranslateKey,
            dangerouslyTranslateWithHTML: translateKey,
            benefits
        } = this.props;
        const unlockedEntitlements: IEntitlement[] = entitlements
            .filter((entitlement: IEntitlement) => !entitlement.hidden)
            .filter( (entitlement: IEntitlement) => entitlement.benefitIds
                .some( benefitId => benefits?.some( benefit => benefit.benefitId === benefitId))
            );

        if (unlockedEntitlements.length === 0) {
            return this.renderRewardPlaceholder();
        }

        const hasMultipleEntitlements: boolean = unlockedEntitlements.length > 1;
        const singleEntitlementName = originalTranslateKey(
            [`${TEXT_KEYS.REDEEM_SUCCESS_GIFT_DESCRIPTION}__${unlockedEntitlements[0]?.name}`, '']);
        const contentClassName = hasMultipleEntitlements
            ? 'sitegen-redeem-success__multiple-items-content'
            : 'sitegen-redeem-success__single-item-content';
        return (
            <div className={contentClassName}>
                <div className="sitegen-redeem-success__content-title">
                    {translateKey(TEXT_KEYS.REDEEM_SUCCESS_TITLE)}
                </div>
                <div className="sitegen-redeem-success__congratulation-message">
                    {hasMultipleEntitlements
                        ? translateKey(TEXT_KEYS.REDEEM_SUCCESS_CONGRATS_MESSAGE_MULTIPLE_ITEMS)
                        : translateKey(TEXT_KEYS.REDEEM_SUCCESS_CONGRATS_MESSAGE_SINGLE_ITEM, { item: singleEntitlementName })
                    }
                </div>
                {unlockedEntitlements.map((unlockedEntitlement: IEntitlement) => {
                    const entitlementName = originalTranslateKey(
                        [`${TEXT_KEYS.REDEEM_SUCCESS_GIFT_DESCRIPTION}__${unlockedEntitlement.name}`, '']);
                    const image: JSX.Element | null = (unlockedEntitlement.imageSrc)
                        ? <img className="sitegen-redeem-gift__image"
                            src={unlockedEntitlement.imageSrc as string}
                            alt={entitlementName}
                            onError={this.onImageError} />
                        : null;
                
                    return (
                        <div key={unlockedEntitlement.id} className={`sitegen-redeem-gift ${unlockedEntitlement.name}`}>
                            {image}

                            <div className="sitegen-redeem-gift__description">
                                <div className="sitegen-redeem-gift__name">
                                    {translateKey(`${TEXT_KEYS.REDEEM_SUCCESS_GIFT_DESCRIPTION}__${unlockedEntitlement.name}`)}
                                </div>
                            </div>
                        </div>
                    ); 
                }
                )}
            </div>
        );
    }

    /** Renders congratulations placeholder for successfully claimed V1 code */
    public renderRewardPlaceholder(): JSX.Element {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        return (
            <>
                <div className="sitegen-redeem-success__content-title">
                    {translateKey(TEXT_KEYS.REDEEM_SUCCESS_TITLE)}
                </div>
                <div className="sitegen-redeem-success__congratulation-message">
                    {translateKey(TEXT_KEYS.REDEEM_SUCCESS_CONGRATS_V1_MESSAGE)}
                </div>
            </>
        );
    }

    private onImageError(e: React.SyntheticEvent<HTMLImageElement>): void {
        const img: HTMLImageElement = (e.target as HTMLImageElement);
        img.onerror = () => {};
        img.style.visibility = 'hidden';
    }
}

RedemptionSuccess.contextType = ConfigContext;

export default translate()(RedemptionSuccess);
